import React, { useEffect, useState } from "react";
import axios from "axios";
import { Flex, Spinner, Text, useColorModeValue } from "@chakra-ui/react";
import Card from "horizon/components/card/Card";

const STAT_FIELDS = [
  { key: "reservation_paid", label: "Payées" },
  { key: "reservation_pending", label: "En attente" },
  { key: "reservation_accepted", label: "Acceptées" },
  { key: "reservation_declined", label: "Refusées" },
];

/**
 * Fetches the income_month payload and pushes counts through the setter.
 * Silent failure: the summary block is non-critical and hides itself on error.
 * @param {Object} args - Fetch arguments
 * @returns {Promise<void>}
 */
async function fetchMonth({ signal, setStats, setError, setLoading }) {
  setLoading(true);
  setError(null);
  try {
    const { data } = await axios.get("/admin/reservation/income_month");
    if (!signal.aborted) setStats(data || null);
  } catch (err) {
    if (!signal.aborted) {
      setError("Stats du mois indisponibles");
      setStats(null);
    }
  } finally {
    if (!signal.aborted) setLoading(false);
  }
}

/**
 * Renders a single mini stat card with a label and a numeric value.
 * @param {Object} props
 * @param {string} props.label - Card label
 * @param {number} props.value - Numeric value
 * @returns {React.ReactElement}
 */
function StatMini({ label, value }) {
  const labelColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
  const valueColor = useColorModeValue("secondaryGray.900", "white");
  const bg = useColorModeValue("secondaryGray.100", "whiteAlpha.100");
  return (
    <Flex direction="column" flex="1" minW="130px" p="12px" borderRadius="12px" bg={bg}>
      <Text fontSize="10px" fontWeight="700" letterSpacing="0.5px" textTransform="uppercase" color={labelColor}>{label}</Text>
      <Text fontSize="xl" fontWeight="700" color={valueColor} mt="4px">{value != null ? value : 0}</Text>
    </Flex>
  );
}

/**
 * Block summarising the current month: 4 mini cards (paid/pending/accepted/declined)
 * driven by GET /admin/reservation/income_month. Hidden when stats are unavailable.
 * @returns {React.ReactElement|null}
 */
function ReservationMonthSummary() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    fetchMonth({ signal: controller.signal, setStats, setError, setLoading });
    return () => controller.abort();
  }, []);

  if (loading && !stats) {
    return <Card><Flex justify="center" py="8px"><Spinner color="brand.500" size="sm" /></Flex></Card>;
  }
  if (error || !stats) return null;
  return (
    <Card>
      <Flex gap="12px" wrap="wrap">
        {STAT_FIELDS.map((field) => <StatMini key={field.key} label={field.label} value={stats[field.key]} />)}
      </Flex>
    </Card>
  );
}

export default ReservationMonthSummary;
